<template>
  <AppSidebar>
    <div class="space-y-8">
      <!-- Page Header -->
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p class="text-gray-600">Manage your account settings and preferences</p>
      </div>
      
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Profile Navigation -->
        <div class="lg:col-span-1">
          <nav class="space-y-2">
            <button
              v-for="tab in profileTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="w-full text-left px-4 py-3 rounded-lg font-medium transition-colors duration-200"
              :class="activeTab === tab.id 
                ? 'bg-primary-50 text-primary-600 border border-primary-200' 
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'"
            >
              <div class="flex items-center space-x-3">
                <component :is="tab.icon" class="w-5 h-5" />
                <span>{{ tab.name }}</span>
              </div>
            </button>
          </nav>
        </div>
        
        <!-- Profile Content -->
        <div class="lg:col-span-2">
          <!-- Personal Information -->
          <div v-if="activeTab === 'personal'" class="card">
            <div class="p-6 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">Personal Information</h2>
            </div>
            <div class="p-6">
              <form @submit.prevent="updateProfile" class="space-y-6">
                <!-- Avatar -->
                <div class="flex items-center space-x-6">
                  <div class="flex-shrink-0">
                    <div class="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center">
                      <span class="text-primary-600 font-bold text-2xl">
                        {{ profileForm.name.charAt(0) }}
                      </span>
                    </div>
                  </div>
                  <div>
                    <button type="button" class="btn btn-outline">
                      Change Avatar
                    </button>
                    <p class="text-sm text-gray-500 mt-1">JPG, GIF or PNG. 1MB max.</p>
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="form-label">Full Name *</label>
                    <input
                      v-model="profileForm.name"
                      type="text"
                      required
                      class="form-input"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div>
                    <label class="form-label">Email Address *</label>
                    <input
                      v-model="profileForm.email"
                      type="email"
                      required
                      class="form-input"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="form-label">Company Name</label>
                    <input
                      v-model="profileForm.company"
                      type="text"
                      class="form-input"
                      placeholder="Your Company"
                    />
                  </div>
                  
                  <div>
                    <label class="form-label">Phone Number</label>
                    <input
                      v-model="profileForm.phone"
                      type="tel"
                      class="form-input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                
                <div>
                  <label class="form-label">Bio</label>
                  <textarea
                    v-model="profileForm.bio"
                    rows="4"
                    class="form-input"
                    placeholder="Tell us about yourself..."
                  ></textarea>
                </div>
                
                <div class="flex justify-end">
                  <button type="submit" class="btn btn-primary">
                    Update Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          <!-- Security Settings -->
          <div v-if="activeTab === 'security'" class="card">
            <div class="p-6 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">Security Settings</h2>
            </div>
            <div class="p-6 space-y-6">
              <!-- Change Password -->
              <form @submit.prevent="changePassword" class="space-y-4">
                <h3 class="text-md font-medium text-gray-900">Change Password</h3>
                
                <div>
                  <label class="form-label">Current Password *</label>
                  <input
                    v-model="passwordForm.currentPassword"
                    type="password"
                    required
                    class="form-input"
                    placeholder="Enter current password"
                  />
                </div>
                
                <div>
                  <label class="form-label">New Password *</label>
                  <input
                    v-model="passwordForm.newPassword"
                    type="password"
                    required
                    class="form-input"
                    placeholder="Enter new password"
                  />
                </div>
                
                <div>
                  <label class="form-label">Confirm New Password *</label>
                  <input
                    v-model="passwordForm.confirmPassword"
                    type="password"
                    required
                    class="form-input"
                    placeholder="Confirm new password"
                  />
                </div>
                
                <div class="flex justify-end">
                  <button type="submit" class="btn btn-primary">
                    Change Password
                  </button>
                </div>
              </form>
              
              <!-- Two-Factor Authentication -->
              <div class="pt-6 border-t border-gray-200">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-md font-medium text-gray-900">Two-Factor Authentication</h3>
                    <p class="text-sm text-gray-500">Add an extra layer of security to your account</p>
                  </div>
                  <button 
                    @click="toggle2FA"
                    class="btn"
                    :class="twoFactorEnabled ? 'btn-outline' : 'btn-primary'"
                  >
                    {{ twoFactorEnabled ? 'Disable' : 'Enable' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Notifications -->
          <div v-if="activeTab === 'notifications'" class="card">
            <div class="p-6 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">Notification Preferences</h2>
            </div>
            <div class="p-6 space-y-6">
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Email Notifications</h3>
                    <p class="text-sm text-gray-500">Receive notifications via email</p>
                  </div>
                  <input
                    v-model="notificationSettings.email"
                    type="checkbox"
                    class="text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
                
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Order Updates</h3>
                    <p class="text-sm text-gray-500">Get notified about order status changes</p>
                  </div>
                  <input
                    v-model="notificationSettings.orderUpdates"
                    type="checkbox"
                    class="text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
                
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Marketing Emails</h3>
                    <p class="text-sm text-gray-500">Receive promotional emails and newsletters</p>
                  </div>
                  <input
                    v-model="notificationSettings.marketing"
                    type="checkbox"
                    class="text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
                
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">SMS Notifications</h3>
                    <p class="text-sm text-gray-500">Receive important updates via SMS</p>
                  </div>
                  <input
                    v-model="notificationSettings.sms"
                    type="checkbox"
                    class="text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
              </div>
              
              <div class="flex justify-end pt-6 border-t border-gray-200">
                <button @click="saveNotificationSettings" class="btn btn-primary">
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
          
          <!-- Billing Information -->
          <div v-if="activeTab === 'billing'" class="card">
            <div class="p-6 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">Billing Information</h2>
            </div>
            <div class="p-6 space-y-6">
              <form @submit.prevent="updateBilling" class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="form-label">Billing Name *</label>
                    <input
                      v-model="billingForm.name"
                      type="text"
                      required
                      class="form-input"
                      placeholder="Full name or company name"
                    />
                  </div>
                  
                  <div>
                    <label class="form-label">Tax ID / VAT Number</label>
                    <input
                      v-model="billingForm.taxId"
                      type="text"
                      class="form-input"
                      placeholder="Tax identification number"
                    />
                  </div>
                </div>
                
                <div>
                  <label class="form-label">Billing Address *</label>
                  <input
                    v-model="billingForm.address"
                    type="text"
                    required
                    class="form-input"
                    placeholder="Street address"
                  />
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label class="form-label">City *</label>
                    <input
                      v-model="billingForm.city"
                      type="text"
                      required
                      class="form-input"
                      placeholder="City"
                    />
                  </div>
                  
                  <div>
                    <label class="form-label">State/Province *</label>
                    <input
                      v-model="billingForm.state"
                      type="text"
                      required
                      class="form-input"
                      placeholder="State or Province"
                    />
                  </div>
                  
                  <div>
                    <label class="form-label">ZIP/Postal Code *</label>
                    <input
                      v-model="billingForm.zipCode"
                      type="text"
                      required
                      class="form-input"
                      placeholder="ZIP or Postal Code"
                    />
                  </div>
                </div>
                
                <div>
                  <label class="form-label">Country *</label>
                  <select v-model="billingForm.country" required class="form-input">
                    <option value="">Select country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
                
                <div class="flex justify-end">
                  <button type="submit" class="btn btn-primary">
                    Update Billing Info
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const authStore = useAuthStore()
const uiStore = useUIStore()

// State
const activeTab = ref('personal')
const twoFactorEnabled = ref(false)

// Profile tabs
const profileTabs = [
  { id: 'personal', name: 'Personal Info', icon: 'PersonIcon' },
  { id: 'security', name: 'Security', icon: 'SecurityIcon' },
  { id: 'notifications', name: 'Notifications', icon: 'NotificationIcon' },
  { id: 'billing', name: 'Billing', icon: 'BillingIcon' }
]

// Forms
const profileForm = reactive({
  name: '',
  email: '',
  company: '',
  phone: '',
  bio: ''
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const billingForm = reactive({
  name: '',
  taxId: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: ''
})

const notificationSettings = reactive({
  email: true,
  orderUpdates: true,
  marketing: false,
  sms: false
})

// Initialize
onMounted(() => {
  loadProfileData()
  loadSettings()
})

// Methods
const loadProfileData = () => {
  if (authStore.user) {
    Object.assign(profileForm, {
      name: authStore.user.name || '',
      email: authStore.user.email || '',
      company: authStore.user.company || '',
      phone: authStore.user.phone || '',
      bio: authStore.user.bio || ''
    })
  }
}

const loadSettings = () => {
  // Load from localStorage
  const savedNotifications = localStorage.getItem('notification_settings')
  if (savedNotifications) {
    Object.assign(notificationSettings, JSON.parse(savedNotifications))
  }
  
  const savedBilling = localStorage.getItem('billing_info')
  if (savedBilling) {
    Object.assign(billingForm, JSON.parse(savedBilling))
  }
  
  twoFactorEnabled.value = localStorage.getItem('two_factor_enabled') === 'true'
}

const updateProfile = async () => {
  try {
    const result = await authStore.updateProfile(profileForm)
    
    if (result.success) {
      uiStore.showNotification({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully.'
      })
    }
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Update Failed',
      message: 'Failed to update profile.'
    })
  }
}

const changePassword = async () => {
  // Validate passwords
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    uiStore.showNotification({
      type: 'error',
      title: 'Password Mismatch',
      message: 'New passwords do not match.'
    })
    return
  }
  
  if (passwordForm.newPassword.length < 6) {
    uiStore.showNotification({
      type: 'error',
      title: 'Password Too Short',
      message: 'Password must be at least 6 characters long.'
    })
    return
  }
  
  // In a real app, this would validate current password and update
  uiStore.showNotification({
    type: 'success',
    title: 'Password Changed',
    message: 'Your password has been changed successfully.'
  })
  
  // Reset form
  Object.assign(passwordForm, {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
}

const toggle2FA = () => {
  twoFactorEnabled.value = !twoFactorEnabled.value
  localStorage.setItem('two_factor_enabled', twoFactorEnabled.value.toString())
  
  uiStore.showNotification({
    type: 'success',
    title: '2FA Updated',
    message: `Two-factor authentication has been ${twoFactorEnabled.value ? 'enabled' : 'disabled'}.`
  })
}

const saveNotificationSettings = () => {
  localStorage.setItem('notification_settings', JSON.stringify(notificationSettings))
  
  uiStore.showNotification({
    type: 'success',
    title: 'Settings Saved',
    message: 'Your notification preferences have been saved.'
  })
}

const updateBilling = () => {
  localStorage.setItem('billing_info', JSON.stringify(billingForm))
  
  uiStore.showNotification({
    type: 'success',
    title: 'Billing Updated',
    message: 'Your billing information has been updated.'
  })
}
</script>

<script>
// Icon components
export default {
  components: {
    PersonIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
        </svg>
      `
    },
    SecurityIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
        </svg>
      `
    },
    NotificationIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM10.07 2.82l-.9.9a4 4 0 000 5.66l.9.9a6 6 0 010 8.49l-.9.9a4 4 0 000 5.66l.9.9"></path>
        </svg>
      `
    },
    BillingIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
        </svg>
      `
    }
  }
}
</script>
